/**
 * useMigrationJobs — TanStack Query hooks for the EdgeMail Migration Tool.
 *
 * PR 5 ships the read hooks (list/detail/dlq) and the lifecycle mutations
 * that have backend support today (createJob, cancelJob). The mutations for
 * unbuilt endpoints are exported now so component wiring can land before the
 * matching backend PR — they will surface 501 errors gracefully via
 * isError + error.response.data.error.message.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EdgeMailAdminAPI } from '../services/EdgeMailAdminAPI';
import type {
  CreateMigrationJobInput,
  ListMigrationJobsOptions,
  MigrationDlqEntry,
} from '../types/migration.types';
import { adminQueryKeys } from './useAdminDashboard';

// ─── Query keys ──────────────────────────────────────────────────────────
export const migrationQueryKeys = {
  all: [...adminQueryKeys.all, 'migration'] as const,
  jobs: (filters?: ListMigrationJobsOptions) =>
    [...migrationQueryKeys.all, 'jobs', filters] as const,
  job: (jobId: string) => [...migrationQueryKeys.all, 'jobs', jobId] as const,
  dlq: (jobId: string, filters?: Record<string, unknown>) =>
    [...migrationQueryKeys.all, 'dlq', jobId, filters] as const,
  verificationReport: (jobId: string) =>
    [...migrationQueryKeys.all, 'verification-report', jobId] as const,
  sourceArchiveView: (messageId: string) =>
    [...migrationQueryKeys.all, 'source-archive', messageId] as const,
};

// ─── Read hooks ──────────────────────────────────────────────────────────
const ACTIVE_LIFECYCLE: ReadonlyArray<string> = ['queued', 'validating', 'running', 'paused', 'awaiting_canary_approval'];

export function useMigrationJobs(opts?: ListMigrationJobsOptions) {
  return useQuery({
    queryKey: migrationQueryKeys.jobs(opts),
    queryFn: () => EdgeMailAdminAPI.listMigrationJobs(opts),
    // Auto-refresh cadence:
    //   - 5s while any job in the snapshot is in flight (canary, running, etc.)
    //   - 30s heartbeat when everything looks settled — never fully off, so a
    //     worker that briefly looked idle between batches always recovers
    //     within one heartbeat without needing the user to focus the window.
    // The flip-to-`false` we had earlier could leave the UI stale for an
    // unbounded time; the 30s floor caps "stuck" perception at half a minute
    // while still being lighter on the API than the 5s active rate.
    refetchInterval: (query) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = query.state.data as any;
      const jobs: Array<{ status?: string }> = Array.isArray(data?.jobs) ? data.jobs : [];
      const anyActive = jobs.some((j) => j?.status ? ACTIVE_LIFECYCLE.includes(j.status) : false);
      return anyActive ? 5_000 : 30_000;
    },
    // Background polling intentionally OFF: when the worker is heavy-lifting
    // (large IMAP migrations on a single shared host), aggressive background
    // polling stacks up backend CPU pressure and contributes to lock-ups.
    // Default behavior (TanStack pauses polling when tab is backgrounded)
    // is the safer choice. On focus, TanStack auto-refetches so the UI
    // catches up immediately.
    staleTime: 5_000,
  });
}

export function useMigrationJob(jobId: string | null | undefined) {
  return useQuery({
    queryKey: jobId ? migrationQueryKeys.job(jobId) : ['migration', 'job', 'disabled'],
    queryFn: () => EdgeMailAdminAPI.getMigrationJob(jobId as string),
    enabled: !!jobId,
    staleTime: 5_000,
  });
}

export function useMigrationDlq(
  jobId: string | null | undefined,
  filters?: { error_code?: string; error_class?: string; suppressed?: boolean; limit?: number; offset?: number },
) {
  return useQuery({
    queryKey: jobId ? migrationQueryKeys.dlq(jobId, filters) : ['migration', 'dlq', 'disabled'],
    queryFn: () => EdgeMailAdminAPI.listMigrationDlq(jobId as string, filters),
    enabled: !!jobId,
    staleTime: 15_000,
  });
}

// ─── Lifecycle mutations ─────────────────────────────────────────────────
function useInvalidateMigration() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: migrationQueryKeys.all });
}

export function useCreateMigrationJob() {
  const invalidate = useInvalidateMigration();
  return useMutation({
    mutationFn: (input: CreateMigrationJobInput) => EdgeMailAdminAPI.createMigrationJob(input),
    onSuccess: invalidate,
  });
}

export function useCancelMigrationJob() {
  const invalidate = useInvalidateMigration();
  return useMutation({
    mutationFn: (jobId: string) => EdgeMailAdminAPI.cancelMigrationJob(jobId),
    onSuccess: invalidate,
  });
}

export function useValidateMigrationJob() {
  const invalidate = useInvalidateMigration();
  return useMutation({
    mutationFn: (jobId: string) => EdgeMailAdminAPI.validateMigrationJob(jobId),
    onSuccess: invalidate,
  });
}

export function usePauseMigrationJob() {
  const invalidate = useInvalidateMigration();
  return useMutation({
    mutationFn: (jobId: string) => EdgeMailAdminAPI.pauseMigrationJob(jobId),
    onSuccess: invalidate,
  });
}

export function useResumeMigrationJob() {
  const invalidate = useInvalidateMigration();
  return useMutation({
    mutationFn: (jobId: string) => EdgeMailAdminAPI.resumeMigrationJob(jobId),
    onSuccess: invalidate,
  });
}

export function usePromoteMigrationCanary() {
  const invalidate = useInvalidateMigration();
  return useMutation({
    mutationFn: (jobId: string) => EdgeMailAdminAPI.promoteMigrationCanary(jobId),
    onSuccess: invalidate,
  });
}

export function useReplayMigrationDlqEntry(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dlqEntry: MigrationDlqEntry) =>
      EdgeMailAdminAPI.replayMigrationDlqEntry(jobId, dlqEntry.dlq_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: migrationQueryKeys.all });
    },
  });
}

export function useReplayMigrationDlqBulk(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filter: { error_code?: string; error_class?: string }) =>
      EdgeMailAdminAPI.replayMigrationDlqBulk(jobId, filter),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: migrationQueryKeys.all });
    },
  });
}

export function useSuppressMigrationDlqEntry(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dlqEntry: MigrationDlqEntry) =>
      EdgeMailAdminAPI.suppressMigrationDlqEntry(jobId, dlqEntry.dlq_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: migrationQueryKeys.all });
    },
  });
}
